import httpClient from './httpClient';

// Sign-in, sign-up and password changes happen in Keycloak (SSO only).
export const authAPI = {
  getSession: async () => {
    const response = await httpClient.get('/auth/session');
    return response.data;
  },
};

// What the caller may use: Business Cases, personas they can act as, WP3
// services and permissions. Computed server-side from the Keycloak token.
export const accessAPI = {
  getMine: async () => {
    const response = await httpClient.get('/access/me');
    return response.data;
  },
};

export const buildingsAPI = {
  getBuildings: async () => {
    const response = await httpClient.get('/buildings');
    return response.data;
  },

  getBuilding: async (id) => {
    const response = await httpClient.get(`/buildings/${id}`);
    return response.data;
  },

  createBuilding: async (payload) => {
    const response = await httpClient.post('/buildings', payload);
    return response.data;
  },

  updateBuilding: async (id, updates) => {
    const response = await httpClient.put(`/buildings/${id}`, updates);
    return response.data;
  },

  deleteBuilding: async (id) => {
    const response = await httpClient.delete(`/buildings/${id}`);
    return response.data;
  },
};

export const profileAPI = {
  getProfile: async () => {
    const response = await httpClient.get('/profile');
    return response.data;
  },

  updateProfile: async (updates) => {
    const response = await httpClient.put('/profile', updates);
    return response.data;
  },

  getRoles: async () => {
    const response = await httpClient.get('/profile/roles');
    return response.data;
  },

  // The caller's personal data as a JSON Blob (GDPR access / portability).
  exportMyData: async () => {
    const response = await httpClient.get('/profile/export', { responseType: 'blob' });
    return response.data;
  },

  // Effective Hub permissions in the active tenant: { tenantId, permissions }.
  getPermissions: async () => {
    const response = await httpClient.get('/profile/permissions');
    return response.data;
  },
};

export const platformAPI = {
  askAssistant: async (payload) => {
    const response = await httpClient.post('/assistant', payload);
    return response.data;
  },

  getIngestionStatus: async () => {
    const response = await httpClient.get('/ingestion/status');
    return response.data;
  },

  searchWiki: async (query) => {
    const response = await httpClient.get('/wiki/search', { params: { q: query } });
    return response.data;
  },

  saveResult: async (payload) => {
    const response = await httpClient.post('/results', payload);
    return response.data;
  },

  listWp3Services: async () => {
    const response = await httpClient.get('/services/registry');
    return response.data;
  },

  invokeWp3Service: async (serviceId, payload) => {
    const response = await httpClient.post(`/services/${serviceId}/invoke`, payload);
    return response.data;
  },

  createEubsoJob: async (payload) => {
    const response = await httpClient.post('/eubso/aggregate', payload);
    return response.data;
  },

  getEubsoJob: async (jobId) => {
    const response = await httpClient.get(`/eubso/aggregate/${jobId}`);
    return response.data;
  },

  // The JSON-LD export as a Blob. Fetched through httpClient because the API
  // authenticates by header, which a plain download link would not send.
  downloadEubsoExport: async (jobId) => {
    const response = await httpClient.get(`/eubso/export/${jobId}`, { responseType: 'blob' });
    return response.data;
  },
};

// The caller's notifications, newest first (the API returns at most 50).
export const notificationsAPI = {
  list: async () => {
    const response = await httpClient.get('/notifications');
    return response.data;
  },

  markRead: async (id) => {
    const response = await httpClient.patch(`/notifications/${id}/read`);
    return response.data;
  },
};

export const dataExchangeAPI = {
  listOffers: async (params = {}) => {
    const response = await httpClient.get('/data-exchange/offers', { params });
    return response.data;
  },

  saveOfferMetadata: async (payload) => {
    const response = await httpClient.post('/data-exchange/offers', payload);
    return response.data;
  },

  markOfferAccepted: async (offerId, payload = {}) => {
    const response = await httpClient.patch(
      `/data-exchange/offers/${offerId}/accepted`,
      payload
    );
    return response.data;
  },

  listTransactions: async (address) => {
    const response = await httpClient.get('/data-exchange/transactions', {
      params: { address },
    });
    return response.data;
  },

  saveTransaction: async (payload) => {
    const response = await httpClient.post('/data-exchange/transactions', payload);
    return response.data;
  },
};

export const blockchainAPI = {
  getRuntime: async () => {
    const response = await httpClient.get('/blockchain/runtime');
    return response.data;
  },

  getOnChainOffers: async () => {
    const response = await httpClient.get('/blockchain/offers');
    return response.data;
  },

  getNotarizationEvents: async (n = 10) => {
    const response = await httpClient.get('/blockchain/notarization/events', { params: { n } });
    return response.data;
  },

  subscribeNotarization: async (payload) => {
    const response = await httpClient.post('/blockchain/notarization/subscribe', payload);
    return response.data;
  },
};

export const agreementsAPI = {
  listAgreements: async (params = {}) => {
    const response = await httpClient.get('/agreements', { params });
    return response.data;
  },

  createAgreement: async (payload) => {
    const response = await httpClient.post('/agreements', payload);
    return response.data;
  },

  getAgreement: async (id) => {
    const response = await httpClient.get(`/agreements/${id}`);
    return response.data;
  },

  updateAgreementStatus: async (id, status, transactionHash) => {
    const response = await httpClient.patch(`/agreements/${id}/status`, {
      status,
      transaction_hash: transactionHash,
    });
    return response.data;
  },
};

export const tenantsAPI = {
  listMine: async () => {
    const response = await httpClient.get('/tenants/me');
    return response.data;
  },

  switchTenant: async ({ tenantId, slug } = {}) => {
    const response = await httpClient.post('/tenants/switch', { tenantId, slug });
    return response.data;
  },

  createTenant: async (payload) => {
    const response = await httpClient.post('/tenants', payload);
    return response.data;
  },

  listMembers: async (tenantId) => {
    const response = await httpClient.get(`/tenants/${tenantId}/members`);
    return response.data;
  },

  updateMember: async (tenantId, userId, payload) => {
    const response = await httpClient.patch(`/tenants/${tenantId}/members/${userId}`, payload);
    return response.data;
  },

  removeMember: async (tenantId, userId) => {
    const response = await httpClient.delete(`/tenants/${tenantId}/members/${userId}`);
    return response.data;
  },

  inviteMember: async (tenantId, payload) => {
    const response = await httpClient.post(`/tenants/${tenantId}/invite`, payload);
    return response.data;
  },

  // params.status: 'pending' | 'accepted' | 'revoked' | 'expired' (optional).
  listInvitations: async (tenantId, params = {}) => {
    const response = await httpClient.get(`/tenants/${tenantId}/invitations`, { params });
    return response.data;
  },

  // Audit trail for organisation admins: { events, nextBefore }.
  // params: { category?, before?, limit? }.
  listActivity: async (tenantId, params = {}) => {
    const response = await httpClient.get(`/tenants/${tenantId}/activity`, { params });
    return response.data;
  },

  revokeInvitation: async (tenantId, inviteId) => {
    const response = await httpClient.delete(`/tenants/${tenantId}/invitations/${inviteId}`);
    return response.data;
  },

  resendInvitation: async (tenantId, inviteId) => {
    const response = await httpClient.post(`/tenants/${tenantId}/invitations/${inviteId}/resend`);
    return response.data;
  },
};

export const dataAssetsAPI = {
  listAssets: async (params = {}) => {
    const response = await httpClient.get('/data-assets', { params });
    return response.data;
  },

  createAsset: async (payload) => {
    const response = await httpClient.post('/data-assets', payload);
    return response.data;
  },

  getAsset: async (id) => {
    const response = await httpClient.get(`/data-assets/${id}`);
    return response.data;
  },
};

export default httpClient;

